import React from 'react';
import { PropTypes as PT } from 'prop-types';
import ReactDOM from 'react-dom';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { Field, reduxForm } from 'redux-form';
import Scroll from 'react-scroll';
import _compact from 'lodash/compact';
import _map from 'lodash/map';
import clipboard from 'clipboard-js';
import { ReactComponent as CancelIcon } from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import { ReactComponent as CheckIcon } from '@lifesize/ux-assets/vectors/svgIcons/check.svg';
import { ReactComponent as EditIcon } from '@lifesize/ux-assets/vectors/svgIcons/edit.svg';
import settingsActions from 'actions/SettingsModal';
import ExpandableInput from 'components/ExpandableInput/ExpandableInput';
import SelectLocale from 'containers/SelectLocale/SelectLocaleContainer';
import logger from 'utils/logger';
import classes from './ProfileSettings.module.scss';

const scroll = Scroll.animateScroll;
let invitationPromise;

const messages = defineMessages({
  copy: {
    id: 'components.profile.copyLink',
    defaultMessage: 'Copy link'
  },
  copied: {
    id: 'components.profile.clipboard.copied',
    defaultMessage: 'Copied!'
  },
  extension: {
    id: 'components.profile.extension',
    defaultMessage: 'extension'
  }
});

export class ProfileSettings extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isCopied: false
    };
  }

  componentDidMount() {
    invitationPromise = this.props.getInviteForExtension();
  }

  componentWillUnmount() {
    this.props.toggleEditName(false);
  }

  copyMeetingInviteLink = () => {
    clipboard.copy(this.props.userProfile.inviteURL).then(() => {
      this.setState({ isCopied: true });
    });
  };

  copyCallingDetails = async () => {
    try {
      const invitation = await invitationPromise;
      clipboard.copy({
        'text/plain': invitation.body
      });
    } catch (err) {
      logger.error(`Unable to copy invitation: ${err}`);
    }
  };

  upload = (acceptedFiles) => {
    // This is a dummy function as there is no nucleus way to actually do this.
    this.props.saveProfileImage(acceptedFiles);
  };

  focus = () => {
    /* the actual textarea is nested two levels deep, so getRenderedComponent() is not adequate here
     * the other options to refer to the DOM node seemed fragile */
    /* eslint-disable react/no-find-dom-node */
    const inputField = ReactDOM.findDOMNode(this.profileName);
    /* eslint-enable react/no-find-dom-node */

    if (!this.props.isEditingProfileName) {
      inputField.focus();
      inputField.select();
      this.props.toggleEditName(true);
    }
  };

  blur = () => {
    this.props.reset();
    this.props.toggleEditName(false);
  };

  profileNameForm = () => {
    const { profileEditEnabled, handleSubmit, isEditingProfileName, pristine, saveContact, userProfile } = this.props;

    if (!profileEditEnabled) {
      return <div className={classes.userNameInput}>{userProfile.name}</div>;
    }

    return (
      <form onSubmit={handleSubmit(saveContact)}>
        <Field
          className={classes.userNameInput}
          component={ExpandableInput}
          maxLength={64}
          name="name"
          onClick={this.focus}
          ref={(formField) => {
            this.profileName = formField;
          }}
          forwardRef
        />
        <div className={classes.buttonContainer}>
          {!isEditingProfileName ? (
            <button onClick={this.focus} key="btnEdit" type="button">
              <EditIcon data-testid="editIcon" />
            </button>
          ) : (
            [
              <button disabled={pristine} key="btnSubmit" type="submit">
                <CheckIcon data-testid="checkIcon" />
              </button>,
              <button onClick={this.blur} key="btnCancel" type="button">
                <CancelIcon data-testid="cancelIcon" />
              </button>
            ]
          )}
        </div>
      </form>
    );
  };

  render() {
    const { isEditingProfileName, profileEditEnabled } = this.props;
    const copyStatus = this.state.isCopied
      ? this.props.intl.formatMessage(messages.copied)
      : this.props.intl.formatMessage(messages.copy);

    return (
      <div className={classes.container}>
        <header>
          <h1>
            <FormattedMessage id="settings.profile.header" defaultMessage="My Profile" />
          </h1>
        </header>
        <div
          className={_compact([
            profileEditEnabled ? classes.nameContainer : classes.inertContainer,
            isEditingProfileName ? classes.isEditing : ''
          ]).join(' ')}
        >
          {this.profileNameForm()}
        </div>
        <section className={classes.profileSection}>
          <dl className={classes.profileDetails}>
            <dt>
              <FormattedMessage id="settings.profile.link" defaultMessage="Meeting link" />
            </dt>
            <dd>
              {this.props.userProfile.inviteURL}
              <button onClick={this.copyMeetingInviteLink}>{copyStatus}</button>
            </dd>
          </dl>
          {this.props.shouldShowPstnNumbers && (
            <dl className={classes.profileDetails}>
              <dt>
                <FormattedMessage id="settings.profile.phone" defaultMessage="Phone" />
              </dt>
              {_map(this.props.pstnNumbers, (number, index) => (
                <dd key={`phoneNumber${index}`}>
                  {`${number.countryName}: ${number.phoneNumber} ${this.props.intl.formatMessage(messages.extension)} ${
                    this.props.extension
                  }`}
                </dd>
              ))}
              <dd>
                <a href={this.props.userProfile.additionalNumbersURL} rel="noopener noreferrer" target="_blank">
                  <FormattedMessage id="settings.profile.additional" defaultMessage="Additional numbers" />
                </a>
              </dd>
            </dl>
          )}
          <dl className={classes.profileDetails}>
            <dt>
              <FormattedMessage id="settings.profile.address" defaultMessage="Video address" />
            </dt>
            <dd>{this.props.userProfile.video}</dd>
          </dl>
          <button onClick={this.copyCallingDetails} className={classes.copyCallDetailsButton}>
            <FormattedMessage id="settings.profile.copyCallDetails" defaultMessage="Copy Calling Details" />
          </button>
          <div className={classes.languageSection}>
            <div className={classes.subHeading}>
              <FormattedMessage id="settings.userSettings.languageTitle" defaultMessage="Language" />
            </div>
            <div className={classes.languageDropdown}>
              <SelectLocale
                dropdownClass={classes.dropdownList}
                onOpen={() => {
                  scroll.scrollToBottom({
                    containerId: this.props.scrollableId,
                    smooth: true,
                    duration: 200
                  });
                }}
              />
            </div>
          </div>
        </section>
      </div>
    );
  }
}

ProfileSettings.propTypes = {
  extension: PT.string,
  getInviteForExtension: PT.func,
  handleSubmit: PT.func,
  intl: PT.object,
  invitation: PT.object,
  isEditingProfileName: PT.bool.isRequired,
  pristine: PT.bool,
  profileEditEnabled: PT.bool.isRequired,
  pstnNumbers: PT.array,
  reset: PT.func,
  saveContact: PT.func.isRequired,
  saveProfileImage: PT.func,
  scrollableId: PT.string,
  shouldShowPstnNumbers: PT.bool,
  toggleEditName: PT.func.isRequired,
  userProfile: PT.object.isRequired
};

export default reduxForm({
  form: 'profileForm',
  fields: ['name'],
  onSubmitSuccess: (values, dispatch) => {
    dispatch(settingsActions.toggleEditName(false));
  },
  getFormState: (state) => state.app.form
})(injectIntl(ProfileSettings));
